import React, { useMemo, useState } from 'react';
import { jsPDF } from 'jspdf';
import { getVendorVerification } from '../../utils/vendorVerification';
import './VendorVerificationBadge.css';

const stateLabel = {
    verified: 'Verified',
    skipped: 'Skipped',
    pending: 'Pending'
};

const imageToDataUrl = async (url) => {
    if (!url) return null;
    try {
        const response = await fetch(url);
        if (!response.ok) return null;
        const blob = await response.blob();
        return await new Promise((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.readAsDataURL(blob);
        });
    } catch {
        return null;
    }
};

export const generateCertificate = async (vendor, verification) => {
    const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
    const width = 297;
    const height = 210;
    const blue = [0, 76, 153];
    const brightBlue = [0, 112, 205];
    const navy = [5, 48, 104];
    const gold = [207, 155, 48];

    // Blue outer frame and clean certificate canvas.
    doc.setFillColor(...blue);
    doc.rect(0, 0, width, height, 'F');
    doc.setFillColor(255, 255, 255);
    doc.rect(5, 5, width - 10, height - 10, 'F');
    doc.setDrawColor(...navy);
    doc.setLineWidth(0.8);
    doc.rect(5, 5, width - 10, height - 10);
    doc.setDrawColor(...gold);
    doc.setLineWidth(0.55);
    doc.rect(10, 10, width - 20, height - 20);

    // Layered blue and gold corner ribbons.
    doc.setFillColor(...navy);
    doc.triangle(5, 5, 92, 5, 5, 60, 'F');
    doc.setFillColor(...brightBlue);
    doc.triangle(5, 33, 68, 5, 5, 70, 'F');
    doc.setDrawColor(...gold);
    doc.setLineWidth(0.8);
    doc.line(5, 48, 61, 5);
    doc.line(10, 58, 82, 5);

    doc.setFillColor(...navy);
    doc.triangle(width - 5, height - 5, width - 92, height - 5, width - 5, height - 60, 'F');
    doc.setFillColor(...brightBlue);
    doc.triangle(width - 5, height - 33, width - 68, height - 5, width - 5, height - 70, 'F');
    doc.setDrawColor(...gold);
    doc.line(width - 5, height - 48, width - 61, height - 5);
    doc.line(width - 10, height - 58, width - 82, height - 5);

    // Subtle dotted decoration in the upper-right area.
    doc.setFillColor(208, 225, 244);
    for (let row = 0; row < 6; row += 1) {
        for (let column = 0; column < 8; column += 1) {
            doc.circle(249 + (column * 4), 15 + (row * 3), 0.35, 'F');
        }
    }

    const logo = await imageToDataUrl('/logo-mangoloops.png');
    if (logo) {
        try {
            const logoProperties = doc.getImageProperties(logo);
            const maxLogoWidth = 60;
            const maxLogoHeight = 23;
            const logoRatio = logoProperties.width / logoProperties.height;
            const logoWidth = Math.min(maxLogoWidth, maxLogoHeight * logoRatio);
            const logoHeight = logoWidth / logoRatio;
            doc.addImage(
                logo,
                logoProperties.fileType,
                (width - logoWidth) / 2,
                13,
                logoWidth,
                logoHeight,
                undefined,
                'FAST'
            );
        } catch {
            // Certificate remains downloadable if the image format is unsupported.
        }
    }

    doc.setTextColor(...navy);
    doc.setFont('times', 'bold');
    doc.setFontSize(27);
    doc.text('Vendor Verification Certificate', width / 2, 55, { align: 'center' });

    doc.setDrawColor(...gold);
    doc.setLineWidth(0.5);
    doc.line(91, 64, 144, 64);
    doc.line(153, 64, 206, 64);
    doc.setFillColor(...gold);
    doc.circle(width / 2, 64, 1.1, 'F');

    doc.setTextColor(40, 40, 40);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(12);
    doc.text('This certificate confirms the registration status of', width / 2, 75, { align: 'center' });
    doc.setTextColor(...navy);
    doc.setFont('times', 'bold');
    doc.setFontSize(24);
    doc.text((vendor.company_name || vendor.name || 'Vendor').toUpperCase(), width / 2, 89, { align: 'center', maxWidth: 190 });

    doc.setDrawColor(...gold);
    doc.setLineWidth(0.45);
    doc.line(94, 96, 203, 96);
    doc.setFillColor(...gold);
    doc.circle(94, 96, 0.8, 'F');
    doc.circle(width / 2, 96, 0.8, 'F');
    doc.circle(203, 96, 0.8, 'F');

    let y = 109;
    verification.checks.forEach((check) => {
        const isPending = check.state === 'pending';
        const isSkipped = check.state === 'skipped';
        doc.setFillColor(isPending ? 224 : isSkipped ? 220 : 20, isPending ? 48 : isSkipped ? 150 : 153, isPending ? 65 : isSkipped ? 35 : 83);
        doc.circle(96, y - 1.5, 4, 'F');
        doc.setDrawColor(255, 255, 255);
        doc.setLineWidth(0.9);
        if (isPending) {
            doc.line(94.4, y - 3.1, 97.6, y + 0.1);
            doc.line(97.6, y - 3.1, 94.4, y + 0.1);
        } else if (isSkipped) {
            doc.line(94, y - 1.5, 98, y - 1.5);
        } else {
            doc.line(93.7, y - 1.5, 95.4, y + 0.2);
            doc.line(95.4, y + 0.2, 98.5, y - 3.5);
        }
        doc.setTextColor(45, 45, 45);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(11.5);
        doc.text(`${check.label}: ${stateLabel[check.state]}${check.detail ? ` (${check.detail})` : ''}`, 104, y);
        y += 11;
    });

    // Bottom band and central verification seal.
    doc.setFillColor(...navy);
    doc.rect(5, 169, width - 10, 9, 'F');
    doc.setDrawColor(...gold);
    doc.setLineWidth(0.6);
    doc.line(5, 168, width - 5, 168);
    doc.line(5, 179, width - 5, 179);

    doc.setFillColor(255, 255, 255);
    doc.circle(width / 2, 173.5, 19.5, 'F');
    doc.setFillColor(...brightBlue);
    doc.circle(width / 2, 173.5, 17.5, 'F');
    doc.setDrawColor(255, 255, 255);
    doc.setLineWidth(0.7);
    doc.circle(width / 2, 173.5, 14.5);
    // Centered shield and checkmark, drawn as vectors for reliable PDF display.
    doc.setLineWidth(0.75);
    doc.line(148.5, 165.5, 155.5, 168.2);
    doc.line(155.5, 168.2, 154.5, 177);
    doc.line(154.5, 177, 148.5, 181.5);
    doc.line(148.5, 181.5, 142.5, 177);
    doc.line(142.5, 177, 141.5, 168.2);
    doc.line(141.5, 168.2, 148.5, 165.5);
    doc.setLineWidth(1.1);
    doc.line(144.2, 173.2, 147.5, 176.5);
    doc.line(147.5, 176.5, 153.4, 169.5);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.setTextColor(255, 255, 255);
    doc.text('VERIFIED', width / 2, 163, { align: 'center' });

    doc.setTextColor(...navy);
    doc.setFont('helvetica', 'bold');
    const registrationDateValue = vendor.registration_date || vendor.created_at || vendor.createdAt;
    const registrationDate = registrationDateValue
        ? new Date(registrationDateValue).toLocaleDateString('en-GB', {
            day: '2-digit', month: 'short', year: 'numeric'
        })
        : 'Not available';

    doc.setDrawColor(...navy);
    doc.setLineWidth(0.65);
    doc.roundedRect(50, 187.5, 6, 6, 0.7, 0.7);
    doc.line(50, 189.5, 56, 189.5);
    doc.line(51.5, 186.8, 51.5, 188.5);
    doc.line(54.5, 186.8, 54.5, 188.5);
    doc.setFontSize(8.5);
    doc.text('Date of Registration', 59, 190);
    doc.setFont('helvetica', 'normal');
    doc.text(registrationDate, 59, 195);

    // Website block aligned opposite the registration date and kept clear of
    // the lower-right corner ribbons.
    doc.setDrawColor(...navy);
    doc.setLineWidth(0.45);
    doc.circle(178, 191.5, 3.2, 'S');
    doc.ellipse(178, 191.5, 1.35, 3.2, 'S');
    doc.line(174.8, 191.5, 181.2, 191.5);

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7.5);
    doc.setTextColor(...navy);
    doc.text('OFFICIAL WEBSITE', 184, 189.7);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9.5);
    doc.text('www.mangoloops.com', 184, 194.2);

    const safeName = (vendor.company_name || vendor.name || 'vendor').replace(/[^a-z0-9]+/gi, '-').replace(/^-|-$/g, '');
    doc.save(`${safeName || 'vendor'}-verification-certificate.pdf`);
};

const VendorVerificationBadge = ({ vendor, kycHistory = [], className = '', onPendingCheckClick }) => {
    const [open, setOpen] = useState(false);
    const verification = useMemo(() => getVendorVerification(vendor, kycHistory), [vendor, kycHistory]);

    return (
        <div className={`vendor-verification ${className}`}>
            <button
                type="button"
                className={`vendor-verification__badge ${verification.fullyVerified ? 'is-verified' : 'is-pending'}`}
                onClick={() => setOpen((currentOpen) => !currentOpen)}
                aria-label="View vendor verification status"
                aria-expanded={open}
            >
                <i className="ri-award-fill" />
                <span className="d-none d-lg-inline">{verification.fullyVerified ? 'Fully Verified' : `${verification.completedCount}/5 Verified`}</span>
            </button>
            <div className={`vendor-verification__tooltip ${open ? 'show' : ''}`} role="tooltip">
                <div className="vendor-verification__heading">
                    <strong>Vendor verification</strong>
                    <button
                        type="button"
                        className="vendor-verification__download"
                        onClick={() => generateCertificate(vendor, verification)}
                        aria-label="Download vendor verification certificate"
                        title="Download certificate"
                    >
                        <i className="ri-download-2-line" aria-hidden="true" />
                    </button>
                </div>
                <span className="vendor-verification__hint">Verification status</span>
                {verification.checks.map((check) => (
                    <button
                        type="button"
                        className={`vendor-verification__row${check.state === 'pending' && onPendingCheckClick ? ' is-actionable' : ''}`}
                        key={check.key}
                        disabled={check.state !== 'pending' || !onPendingCheckClick}
                        onClick={() => onPendingCheckClick?.(check)}
                        aria-label={check.state === 'pending' ? `Complete ${check.label}` : `${check.label}: ${stateLabel[check.state]}`}
                    >
                        <i className={check.state === 'verified' ? 'ri-checkbox-circle-fill verified' : check.state === 'skipped' ? 'ri-subtract-line skipped' : 'ri-close-circle-fill pending'} />
                        <span>{check.label}</span>
                        <small className={check.state}>{stateLabel[check.state]}</small>
                    </button>
                ))}
            </div>
        </div>
    );
};

export default VendorVerificationBadge;
